import { detectIsIOS, type NavigatorLike } from './deviceDetection'

const CANONICAL_REPORT_PATH_REGEX = /^\/report\/([a-zA-Z0-9_-]+)$/
const CANONICAL_ARTICLE_PATH_REGEX = /^\/article\/(\d+)$/

export const APP_OPEN_FALLBACK_TIMEOUT_MS = 2500

type WindowLike = {
  location?: {
    href: string
  }
  setTimeout?: typeof window.setTimeout
  clearTimeout?: typeof window.clearTimeout
}

type DocumentLike = {
  hidden?: boolean
  visibilityState?: DocumentVisibilityState
  addEventListener?: (type: 'visibilitychange', listener: () => void) => void
  removeEventListener?: (type: 'visibilitychange', listener: () => void) => void
}

type TryOpenInAppOptions = {
  documentLike?: DocumentLike
  navigatorLike?: NavigatorLike
  timeoutMs?: number
  windowLike?: WindowLike
}

function detectIsAndroid(navigatorLike?: NavigatorLike): boolean {
  const nav =
    navigatorLike ?? (typeof navigator !== 'undefined' ? (navigator as NavigatorLike) : undefined)

  if (!nav) {
    return false
  }

  return /Android/i.test(nav.userAgent || '')
}

function getVisibilityState(documentLike: DocumentLike | undefined): 'hidden' | 'visible' {
  if (!documentLike) {
    return 'visible'
  }

  if (documentLike.visibilityState === 'hidden' || documentLike.hidden) {
    return 'hidden'
  }

  return 'visible'
}

export function supportsNativeAppOpen(navigatorLike?: NavigatorLike): boolean {
  return detectIsIOS(navigatorLike) || detectIsAndroid(navigatorLike)
}

export function resolveEmailAppOpenUrl(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search)
  if (params.get('source') !== 'email') {
    return null
  }

  const reportPathMatch = pathname.match(CANONICAL_REPORT_PATH_REGEX)
  if (reportPathMatch) {
    return `insightatlas://report/${reportPathMatch[1]}`
  }

  const articlePathMatch = pathname.match(CANONICAL_ARTICLE_PATH_REGEX)
  if (articlePathMatch) {
    return `insightatlas://article/${articlePathMatch[1]}`
  }

  return null
}

export function tryOpenInApp(
  appUrl: string,
  options: TryOpenInAppOptions = {}
): Promise<boolean> {
  const navigatorLike =
    options.navigatorLike ?? (typeof navigator !== 'undefined' ? (navigator as NavigatorLike) : undefined)
  const windowLike =
    options.windowLike ?? (typeof window !== 'undefined' ? (window as unknown as WindowLike) : undefined)
  const documentLike =
    options.documentLike ?? (typeof document !== 'undefined' ? (document as unknown as DocumentLike) : undefined)
  const timeoutMs = options.timeoutMs ?? APP_OPEN_FALLBACK_TIMEOUT_MS

  if (!appUrl || !windowLike?.location || !documentLike || !supportsNativeAppOpen(navigatorLike)) {
    return Promise.resolve(false)
  }

  if (getVisibilityState(documentLike) === 'hidden') {
    return Promise.resolve(true)
  }

  const location = windowLike.location

  return new Promise((resolve) => {
    let settled = false
    let timeoutId: number | null = null

    const cleanup = (opened: boolean) => {
      if (settled) {
        return
      }

      settled = true
      if (timeoutId !== null) {
        windowLike.clearTimeout?.(timeoutId)
      }
      documentLike.removeEventListener?.('visibilitychange', handleVisibilityChange)
      resolve(opened)
    }

    const handleVisibilityChange = () => {
      if (getVisibilityState(documentLike) === 'hidden') {
        cleanup(true)
      }
    }

    documentLike.addEventListener?.('visibilitychange', handleVisibilityChange)
    timeoutId = windowLike.setTimeout?.(
      () => cleanup(getVisibilityState(documentLike) === 'hidden'),
      timeoutMs
    ) ?? null

    location.href = appUrl
  })
}
